import { ACTION_STATUS_LABELS, voteTargetKey, type RetroSnapshot } from '@retro/shared';

function download(filename: string, mime: string, contents: string): void {
  const blob = new Blob([contents], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function slug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

function votesFor(snapshot: RetroSnapshot, comment: { id: string; groupId: string | null }): number {
  const key = comment.groupId
    ? voteTargetKey({ type: 'GROUP', id: comment.groupId })
    : voteTargetKey({ type: 'COMMENT', id: comment.id });
  return snapshot.votes.totals[key] ?? 0;
}

/** RFC 4180 quoting: double the quotes and wrap anything risky. */
function csvCell(value: string | number): string {
  const text = String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

export function downloadReportCsv(snapshot: RetroSnapshot): void {
  const rows: (string | number)[][] = [
    ['Column', 'Group', 'Comment', 'Author', 'Votes', 'Topics'],
  ];

  const sorted = [...snapshot.comments].sort((a, b) => votesFor(snapshot, b) - votesFor(snapshot, a));

  for (const comment of sorted) {
    rows.push([
      snapshot.columns.find((column) => column.id === comment.columnId)?.title ?? '',
      comment.groupId
        ? (snapshot.groups.find((group) => group.id === comment.groupId)?.title || 'Grouped comments')
        : '',
      comment.text,
      comment.isAnonymous ? 'Anonymous' : (comment.author?.name ?? 'Unknown'),
      votesFor(snapshot, comment),
      comment.topicIds
        .map((id) => snapshot.topics.find((topic) => topic.id === id)?.name)
        .filter(Boolean)
        .join(' | '),
    ]);
  }

  rows.push([]);
  rows.push(['Action item', 'Owner', 'Status', 'Due date', 'Description']);
  for (const action of snapshot.actionItems) {
    rows.push([
      action.title,
      action.assignee?.name ?? '',
      ACTION_STATUS_LABELS[action.status],
      action.dueDate ? new Date(action.dueDate).toLocaleDateString() : '',
      action.description,
    ]);
  }

  const csv = rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
  download(`${slug(snapshot.retro.name)}-report.csv`, 'text/csv', csv);
}

export function downloadReportMarkdown(snapshot: RetroSnapshot): void {
  const lines: string[] = [];
  const created = new Date(snapshot.retro.createdAt).toLocaleDateString();

  lines.push(`# ${snapshot.retro.name}`, '');
  lines.push(`**Team:** ${snapshot.retro.teamName}  `);
  lines.push(`**Template:** ${snapshot.retro.templateName}  `);
  lines.push(`**Date:** ${created}`, '');

  lines.push(`## Participants (${snapshot.participants.length})`, '');
  for (const participant of snapshot.participants) {
    lines.push(`- ${participant.avatar} ${participant.name}${participant.kind === 'GUEST' ? ' (guest)' : ''}`);
  }
  lines.push('');

  lines.push(`## Action items (${snapshot.actionItems.length})`, '');
  if (snapshot.actionItems.length === 0) {
    lines.push('_None captured._', '');
  } else {
    for (const action of snapshot.actionItems) {
      const owner = action.assignee ? ` — **${action.assignee.name}**` : '';
      const due = action.dueDate ? ` (due ${new Date(action.dueDate).toLocaleDateString()})` : '';
      lines.push(`- [${action.status === 'DONE' ? 'x' : ' '}] ${action.title}${owner}${due}`);
      if (action.description) {
        for (const paragraph of action.description.split('\n')) lines.push(`  ${paragraph}`);
      }
    }
    lines.push('');
  }

  lines.push('## Comments', '');
  for (const column of snapshot.columns) {
    const columnComments = snapshot.comments
      .filter((comment) => comment.columnId === column.id)
      .sort((a, b) => votesFor(snapshot, b) - votesFor(snapshot, a));
    if (columnComments.length === 0) continue;

    lines.push(`### ${column.emoji} ${column.title}`, '');
    for (const comment of columnComments) {
      const votes = votesFor(snapshot, comment);
      const author = comment.isAnonymous ? 'Anonymous' : (comment.author?.name ?? 'Unknown');
      const topics = comment.topicIds
        .map((id) => snapshot.topics.find((topic) => topic.id === id)?.name)
        .filter(Boolean);
      const suffix = topics.length > 0 ? ` _[${topics.join(', ')}]_` : '';
      lines.push(`- **${votes}👍** ${comment.text.replace(/\n/g, ' ')} — ${author}${suffix}`);
    }
    lines.push('');
  }

  if (snapshot.roti && snapshot.roti.resultsVisible && snapshot.roti.responseCount > 0) {
    lines.push('## ROTI', '');
    lines.push(`Average **${snapshot.roti.average}** from ${snapshot.roti.responseCount} responses.`, '');
    for (const entry of snapshot.roti.comments) {
      lines.push(`- **${entry.score}/5** — ${entry.comment.replace(/\n/g, ' ')}`);
    }
    lines.push('');
  }

  download(`${slug(snapshot.retro.name)}-report.md`, 'text/markdown', lines.join('\n'));
}
